import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useServerFn } from "@tanstack/react-start";
import { Check, Loader2 } from "lucide-react";
import {
  contactMessageSchema,
  contactSubjects,
  createDefaultContactValues,
  stayRelatedSubjects,
  type ContactMessage,
} from "@/lib/contact-schema";
import { trackEvent } from "@/lib/analytics";
import { resort } from "@/data/resort";
import { submitContactMessage } from "@/functions/submit-contact";
import { ResortButton, ResortLink } from "../resort-button";
import { TextInput, TextSelect, TextTextarea } from "../reservations/FormField";
import { fieldError } from "../reservations/form-styles";

export function ContactForm() {
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{ requestId: string; firstName: string } | null>(null);
  const startedRef = useRef(false);
  const submitFn = useServerFn(submitContactMessage);

  const form = useForm<ContactMessage>({
    resolver: zodResolver(contactMessageSchema),
    defaultValues: createDefaultContactValues(),
    mode: "onSubmit",
  });

  const subject = form.watch("subject");
  const showStayNudge = Boolean(subject && stayRelatedSubjects.includes(subject));

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    trackEvent("contact_form_started");
  }, []);

  const onSubmit = form.handleSubmit(async (values) => {
    if (submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const result = await submitFn({ data: values });
      if (!result?.ok) {
        if (result?.fieldErrors) {
          for (const [key, message] of Object.entries(result.fieldErrors)) {
            form.setError(key as keyof ContactMessage, { type: "server", message });
          }
        }
        setSubmitError(
          result?.error ||
            "We couldn't send your message. Please try again or contact Auburn RV Resort directly.",
        );
        return;
      }
      trackEvent("contact_form_submitted", { subject_category: values.subject });
      setSuccess({ requestId: result.requestId, firstName: values.firstName });
      document.getElementById("contact-form")?.scrollIntoView({ behavior: "smooth" });
    } catch {
      setSubmitError(
        "We couldn't send your message. Please try again or contact Auburn RV Resort directly.",
      );
    } finally {
      setSubmitting(false);
    }
  });

  if (success) {
    return (
      <section id="contact-form" className="scroll-mt-16 bg-secondary/30 py-10 md:py-16">
        <div className="shell max-w-2xl">
          <div
            className="border border-border bg-card px-6 py-8 text-center md:px-10"
            role="status"
            aria-live="polite"
          >
            <div className="mx-auto flex size-14 items-center justify-center rounded-full border border-primary/30 bg-primary/5">
              <Check className="size-6 text-primary" strokeWidth={1.5} />
            </div>
            <p className="eyebrow mt-8 text-primary">Message Sent</p>
            <h2 className="display-md mt-4">Thanks, {success.firstName}. We Got Your Message.</h2>
            <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-muted-foreground">
              Your message has been sent to Auburn RV Resort.
            </p>
            <p className="eyebrow mt-8 text-muted-foreground">Reference</p>
            <p className="font-display mt-2 text-2xl tracking-tight">{success.requestId}</p>
            <div className="mt-10 flex flex-col justify-center gap-3 sm:flex-row">
              <ResortLink variant="solid" href="/" className="w-full sm:w-auto">
                Return Home
              </ResortLink>
              <ResortLink variant="outline" href="/reservations/" className="w-full sm:w-auto">
                Check Availability
              </ResortLink>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      id="contact-form"
      aria-labelledby="contact-form-heading"
      className="scroll-mt-16 bg-secondary/30 py-10 md:py-16"
    >
      <div className="shell max-w-2xl">
        <div className="border border-border bg-card p-6 shadow-soft md:p-10">
          <h2 id="contact-form-heading" className="display-md">
            Send Us a Message
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
            Complete the form below and we&apos;ll send your message directly to Auburn RV Resort.
          </p>

          <form className="mt-10 space-y-6" onSubmit={onSubmit} noValidate>
            <div className="absolute -left-[9999px] h-0 w-0 overflow-hidden" aria-hidden="true">
              <label htmlFor="companyWebsite">Company website</label>
              <input
                id="companyWebsite"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                {...form.register("companyWebsite")}
              />
            </div>

            <div className="grid gap-6 sm:grid-cols-2">
              <TextInput
                id="firstName"
                label="First Name"
                required
                autoComplete="given-name"
                error={form.formState.errors.firstName?.message}
                {...form.register("firstName")}
              />
              <TextInput
                id="lastName"
                label="Last Name"
                required
                autoComplete="family-name"
                error={form.formState.errors.lastName?.message}
                {...form.register("lastName")}
              />
            </div>

            <TextInput
              id="email"
              label="Email Address"
              required
              type="email"
              autoComplete="email"
              error={form.formState.errors.email?.message}
              {...form.register("email")}
            />

            <TextInput
              id="phone"
              label="Phone Number"
              type="tel"
              autoComplete="tel"
              help="Optional — include your number if you'd prefer a call back."
              error={form.formState.errors.phone?.message}
              {...form.register("phone")}
            />

            <TextSelect
              id="subject"
              label="Subject"
              required
              placeholder="Select a subject"
              error={form.formState.errors.subject?.message}
              options={contactSubjects.map((s) => ({ value: s, label: s }))}
              {...form.register("subject")}
            />

            {showStayNudge ? (
              <div className="border border-border bg-secondary/40 p-5">
                <p className="eyebrow text-primary">Planning a stay?</p>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  Our Availability Request form collects the dates, RV details and party information
                  our team needs to review availability.
                </p>
                <ResortLink variant="outline" href="/reservations/" className="mt-4 w-full sm:w-auto">
                  Check Availability
                </ResortLink>
              </div>
            ) : null}

            <TextTextarea
              id="message"
              label="Comment or Message"
              required
              rows={6}
              placeholder="How can we help?"
              error={form.formState.errors.message?.message}
              {...form.register("message")}
            />

            {submitError ? (
              <div
                className="border border-destructive/30 bg-background p-5"
                role="alert"
                aria-live="assertive"
              >
                <p className="eyebrow text-destructive">We Couldn&apos;t Send Your Message</p>
                <p className="mt-2 text-sm text-muted-foreground">{submitError}</p>
                <p className="mt-2 text-sm text-muted-foreground">
                  Your information has not been lost. Please try again or contact Auburn RV Resort
                  directly.
                </p>
                <div className="mt-4 flex flex-col gap-2 text-sm sm:flex-row sm:gap-5">
                  <a href={resort.phoneHref} className="hover:text-primary">
                    {resort.phone}
                  </a>
                  <a href={`mailto:${resort.email}`} className="hover:text-primary">
                    {resort.email}
                  </a>
                </div>
              </div>
            ) : null}

            <ResortButton
              type="submit"
              variant="solid"
              className="w-full sm:w-auto sm:min-w-[220px]"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Loader2 className="size-4 animate-spin" strokeWidth={1.6} />
                  Sending...
                </>
              ) : (
                "Send Message"
              )}
            </ResortButton>
            {form.formState.errors.root ? (
              <p role="alert" className={fieldError}>
                {form.formState.errors.root.message}
              </p>
            ) : null}
          </form>
        </div>
      </div>
    </section>
  );
}
